import { GetString } from "fluent-react";
import React, { RefObject, useEffect, useState } from "react";
import styled from "styled-components";
import { rootFontSize } from "../../cssVariables";
import {
  CountryProductYear,
  CountryProfile,
  CountryYear,
  LookBackYearRange,
  PolicyRecommendation,
  Product,
  ProductClass,
  ProductLevel,
  ProductYear,
  TreeMapInput,
  TreeMapProduct,
  TreeMapType,
} from "../../graphQL/types/shared";
import { gql, useQuery } from "../../graphQL/useQuery";
import { SlideElementProps } from "../../presentationManager/PresentationManager";
import Loading from "../../sharedComponents/GraphLoading";
import useLastSuccesfulFetch from "../../tree/newChart/useLastSuccesfulFetch";
import {
  GoToConclusionButton,
  GoToConclusionText,
  GoToConclusionTextWrapper,
} from "../feasibilityChart";
import {
  GraphAreaContainerExtendUp as GraphAreaContainer,
  searchBarHeight,
  searchBarHeightSmall,
  smallMediaQuery,
} from "../Grid";
import HelperText from "../topProductsTable/HelperText";
import { PolicyDimension } from "../Utils";
import Table from "./Table";
import transform from "./transform";

const localZIndices = {
  table: 10,
  spinner: 20,
  helperText: 30,
};

const GhostTable = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin-top: ${searchBarHeight}rem;
  max-height: 100%;

  svg {
    .cls-1,
    .cls-2 {
      fill: none;
      stroke: #e5e5e5;
      stroke-miterlimit: 10;
    }

    .cls-1 {
      stroke-width: 5px;
    }

    .cls-2 {
      stroke-width: 2px;
    }

    .cls-3 {
      fill: #e5e5e5;
    }
  }

  @media ${smallMediaQuery} {
    margin-top: ${searchBarHeightSmall}rem;
  }
`;

const query = gql`
  query TopProductsTable(
    $location: ID!
    $year: Int!
    $facet: TreeMapType!
    $productClass: ProductClass!
    $productLevel: ProductLevel!
    $locationLevel: LocationLevel
    $product: ID
    $partner: ID
  ) {
    location: countryProfile(location: $location) {
      policyRecommendation
      latestECIRank: latestEciRank
      latestGDPPerCapita: latestGdpPerCapita {
        quantity
      }
    }
    countryYear(location: $location, year: $year) {
      eci
    }

    products: treeMap(
      facet: $facet
      productClass: $productClass
      year: $year
      productLevel: $productLevel
      locationLevel: $locationLevel
      location: $location
      product: $product
      partner: $partner
      mergePci: false
    ) {
      ... on TreeMapProduct {
        product {
          id
          shortName
          level
          code
          neverShow: hideFeasibility
          topLevelParent {
            id
          }
        }
        normalizedPCI: normalizedPci
        normalizedDistance
        normalizedOpportunityGain
        rca
      }
    }

    allProductYear(
      productClass: $productClass
      productLevel: $productLevel
      year: $year
    ) {
      pci
      globalExportValue
      globalExportValueChange: globalExportValueChangeFiveYears
      product {
        id
      }
    }

    allCountryProductYear(
      location: $location
      year: $year
      productClass: $productClass
      productLevel: $productLevel
    ) {
      normalizedOpportunityGainDecileClassification
      normalizedDistanceDecileClassification
      normalizedPCIDecileClassification: normalizedPciDecileClassification
      product {
        id
      }
    }
  }
`;
export interface FetchedTreeDatum {
  product: {
    id: TreeMapProduct["product"]["id"];
    shortName: TreeMapProduct["product"]["shortName"];
    level: TreeMapProduct["product"]["level"];
    code: TreeMapProduct["product"]["code"];
    neverShow: TreeMapProduct["product"]["hideFeasibility"];
    topLevelParent: {
      id: TreeMapProduct["product"]["topLevelParent"]["id"];
    };
  };
  normalizedPCI: TreeMapProduct["normalizedPci"];
  normalizedDistance: TreeMapProduct["normalizedDistance"];
  normalizedOpportunityGain: TreeMapProduct["normalizedOpportunityGain"];
  rca: TreeMapProduct["rca"];
}

export interface FetchedProductYearDatum {
  product: {
    id: Product["id"];
  };
  pci: ProductYear["pci"];
  globalExportValue: ProductYear["globalExportValue"];
  globalExportValueChange: ProductYear["globalExportValueChangeFiveYears"];
}

export interface FetchedCountryProductYearDatum {
  product: {
    id: Product["id"];
  };
  normalizedOpportunityGainDecileClassification: CountryProductYear["normalizedOpportunityGainDecileClassification"];
  normalizedDistanceDecileClassification: CountryProductYear["normalizedDistanceDecileClassification"];
  normalizedPCIDecileClassification: CountryProductYear["normalizedPciDecileClassification"];
}

interface SuccessResponse {
  location: null | {
    latestECIRank: CountryProfile["latestEciRank"];
    policyRecommendation: CountryProfile["policyRecommendation"];
    latestGDPPerCapita: CountryProfile["latestGdpPerCapita"];
  };
  countryYear: {
    eci: CountryYear["eci"];
  };
  products: FetchedTreeDatum[];
  allProductYear: FetchedProductYearDatum[];
  allCountryProductYear: FetchedCountryProductYearDatum[];
}

interface Variables extends TreeMapInput {
  yearRange: LookBackYearRange;
}

interface ExtraInputs {
  policyDimension: PolicyDimension;
  zIndexFromParent: number;
  getFluentString: GetString;
  year: number;
  rootElRef: RefObject<HTMLDivElement>;
  goToConclusion: () => void;
}

interface ContainerProps {
  extraInputs: ExtraInputs;
  variables: Variables;
}

interface DOMMeasurement {
  width: number;
  height: number;
}

const tableKey = "table";
const spinnerKey = "spinner";

const Container = (props: ContainerProps) => {
  const {
    extraInputs: {
      policyDimension,
      getFluentString,
      zIndexFromParent,
      year,
      rootElRef,
      goToConclusion,
    },
    variables,
    extraInputs,
  } = props;
  const { data, loading, error } = useQuery<SuccessResponse, Variables>(query, {
    variables,
  });
  const [size, setSize] = useState<DOMMeasurement | undefined>(undefined);

  useEffect(() => {
    const { current: el } = rootElRef;
    if (el !== null) {
      const { width, height } = el.getBoundingClientRect();
      setSize({
        width: Math.floor(width),
        height: Math.floor(height - searchBarHeight * rootFontSize * 1.25),
      });
    }
  }, []);
  let output: React.ReactElement<any> | null;
  let helperText: React.ReactElement<any> | null;
  const lastSuccessfulFetch = useLastSuccesfulFetch<
    SuccessResponse,
    Variables,
    ExtraInputs
  >({
    inputs: { queryResult: { loading, error, data, variables }, extraInputs },
    isFetchedDataEmpty: ({
      location,
      products,
      countryYear,
      allProductYear,
      allCountryProductYear,
    }) =>
      location === null ||
      countryYear === null ||
      products.length === 0 ||
      allProductYear.length === 0 ||
      allCountryProductYear.length === 0,
  });
  if (loading === true) {
    if (lastSuccessfulFetch !== undefined && size !== undefined) {
      const {
        location,
        products,
        countryYear,
        allProductYear,
        allCountryProductYear,
      } = lastSuccessfulFetch.data;
      if (
        location !== null &&
        countryYear !== null &&
        countryYear.eci !== null &&
        location.policyRecommendation !== null &&
        size !== undefined &&
        location.latestECIRank !== null &&
        location.latestGDPPerCapita &&
        location.latestGDPPerCapita.quantity
      ) {
        if (
          location.policyRecommendation === PolicyRecommendation.TechFrontier
        ) {
          output = (
            <>
              <GhostTable
                dangerouslySetInnerHTML={{
                  __html: require("./ghost_table.svg"),
                }}
              />
              <GoToConclusionTextWrapper>
                <GoToConclusionText>
                  {getFluentString("early-exit-viz-not-available-text")}
                </GoToConclusionText>
                <GoToConclusionButton onClick={goToConclusion}>
                  {getFluentString("early-exit-viz-button-text")}
                </GoToConclusionButton>
              </GoToConclusionTextWrapper>
            </>
          );
          helperText = null;
        } else {
          const tableData = transform({
            products,
            policyDimension,
            policyRecommendation: location.policyRecommendation,
            countryECI: countryYear.eci,
            gdpPerCaptia: location.latestGDPPerCapita.quantity,
            allProductYear,
            allCountryProductYear,
          });
          const { width, height } = size;
          output = (
            <>
              <Table
                products={tableData}
                getFluentString={getFluentString}
                width={width}
                height={height}
                zIndexFromParent={localZIndices.table}
                year={year}
              />
              <Loading key={spinnerKey} zIndex={localZIndices.spinner} />
            </>
          );
          helperText = (
            <HelperText
              getFluentString={getFluentString}
              zIndex={localZIndices.helperText}
            />
          );
        }
      } else {
        output = <Loading key={spinnerKey} />;
        helperText = null;
      }
    } else {
      output = <Loading key={spinnerKey} />;
      helperText = null;
    }
  } else if (error !== undefined) {
    console.error(error);
    output = null;
    helperText = null;
  } else if (data !== undefined) {
    const {
      location,
      products,
      countryYear,
      allProductYear,
      allCountryProductYear,
    } = data;
    if (
      location !== null &&
      countryYear !== null &&
      countryYear.eci !== null &&
      location.policyRecommendation !== null &&
      size !== undefined &&
      location.latestECIRank !== null &&
      location.latestGDPPerCapita &&
      location.latestGDPPerCapita.quantity
    ) {
      if (location.policyRecommendation === PolicyRecommendation.TechFrontier) {
        output = (
          <>
            <GhostTable
              dangerouslySetInnerHTML={{ __html: require("./ghost_table.svg") }}
            />
            <GoToConclusionTextWrapper>
              <GoToConclusionText>
                {getFluentString("early-exit-viz-not-available-text")}
              </GoToConclusionText>
              <GoToConclusionButton onClick={goToConclusion}>
                {getFluentString("early-exit-viz-button-text")}
              </GoToConclusionButton>
            </GoToConclusionTextWrapper>
          </>
        );
        helperText = null;
      } else {
        const tableData = transform({
          products,
          policyDimension,
          policyRecommendation: location.policyRecommendation,
          countryECI: countryYear.eci,
          gdpPerCaptia: location.latestGDPPerCapita.quantity,
          allProductYear,
          allCountryProductYear,
        });

        const { width, height } = size;
        output = (
          <Table
            products={tableData}
            getFluentString={getFluentString}
            width={width}
            height={height}
            key={tableKey}
            zIndexFromParent={localZIndices.table}
            year={year}
          />
        );
        helperText = (
          <HelperText
            getFluentString={getFluentString}
            zIndex={localZIndices.helperText}
          />
        );
      }
    } else {
      output = null;
      helperText = null;
    }
  } else {
    output = null;
    helperText = null;
  }

  const style: React.CSSProperties = {
    zIndex: zIndexFromParent,
  };
  return (
    <>
      <GraphAreaContainer style={style} ref={rootElRef}>
        {helperText}
        {output}
      </GraphAreaContainer>
    </>
  );
};

type Props = Omit<ExtraInputs, "rootElRef"> &
  SlideElementProps & {
    year: number;
    countryId: string;
  };

const QueryWrapper = React.forwardRef(
  (props: Props, rootElRef: RefObject<HTMLDivElement>) => {
    const {
      year,
      countryId,
      policyDimension,
      zIndexFromParent,
      getFluentString,
      goToConclusion,
    } = props;
    const variables: Variables = {
      facet: TreeMapType.CPY_C,
      productClass: ProductClass.HS,
      year,
      productLevel: ProductLevel.fourDigit,
      locationLevel: null,
      location: countryId,
      product: null,
      partner: null,
      yearRange: LookBackYearRange.FiveYears,
    };

    return (
      <Container
        extraInputs={{
          policyDimension,
          zIndexFromParent,
          getFluentString,
          year,
          rootElRef,
          goToConclusion,
        }}
        variables={variables}
      />
    );
  }
);

export default QueryWrapper;
